import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core"
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import classNames from "classnames"

import { colors } from "@/components/colors"
import TextField from "@/components/form/textField/TextField"
import BasicButton from "@/components/button/BasicButton"
import IconButton from "@/components/button/IconButton"
import Icon from "@/components/icon/Icon"
import Tooltip from "@/components/tooltip/Tooltip"
import { useMetricList } from "@/hooks/api/useMetricQuery"
import { MetricMeta } from "@/types/metric"
import { MetricThreshold } from "@/types/threshold"
import { StatsSettings } from "@/app/report/common/type"

import "./metricSelectorTable.scss"
import { useTranslateMetric } from "@/hooks/metric/useTranslateKeyHook"

type SortableItemProps = {
  id: string
  label: string
}

const DEFAULT_FIXED_COLUMNS = [
  { key: "jersey", label: "jersey" },
  { key: "playerName", label: "playerName" },
  { key: "position", label: "position" },
]

export default function MetricSelectorTable({
  settings,
  setTempSelectedMetricKeyList,
  sortSetting,
  onSortChange,
  fixedColumns = DEFAULT_FIXED_COLUMNS,
  additionalItems,
  thresholds,
  onThresholdClick,
}: {
  settings: StatsSettings
  setTempSelectedMetricKeyList: (value: string[]) => void
  sortSetting?: { key: string; order: "asc" | "desc" }
  onSortChange?: (value: { key: string; order: "asc" | "desc" }) => void
  fixedColumns?: Array<{ key: string; label: string }>
  additionalItems?: Array<{ key: string; label: string }>
  thresholds?: MetricThreshold[]
  onThresholdClick?: (metricKey: string, metricName: string) => void
}) {
  const { t } = useTranslation()
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )
  const translateMetric = useTranslateMetric()

  const [metricList, setMetricList] = useState<MetricMeta[]>([])
  const [selectedMetricList, setSelectedMetricList] = useState<MetricMeta[]>([])
  const [searchValue, setSearchValue] = useState<string>("")
  const [selectedAdditionalKeys, setSelectedAdditionalKeys] = useState<string[]>(() => {
    return settings.metrics.filter((key) => additionalItems?.some((item) => item.key === key))
  })

  const unselectedAdditionalItems = additionalItems?.filter(
    (item) => !selectedAdditionalKeys.includes(item.key),
  )

  const { data } = useMetricList()

  useEffect(() => {
    onResetData()
    if (additionalItems) {
      setSelectedAdditionalKeys(
        settings.metrics.filter((key) => additionalItems.some((item) => item.key === key)),
      )
    }
  }, [data, settings, additionalItems])

  useEffect(() => {
    onSearchMetric()
  }, [searchValue])

  useEffect(() => {
    setTempSelectedMetricKeyList([
      ...selectedAdditionalKeys,
      ...selectedMetricList.map((item) => item.key),
    ])
  }, [selectedMetricList, selectedAdditionalKeys])

  const onSearchMetric = (prev?: MetricMeta[]) => {
    const defaultMetrics =
      prev || data?.filter((dt) => !selectedMetricList.some((sm) => sm.key === dt.key)) || []

    if (!searchValue || searchValue.length < 2) {
      setMetricList(defaultMetrics)

      return
    }
    const filteredItems = defaultMetrics.filter((item) => {
      const translatedText = translateMetric(item)?.toLowerCase() || ""
      const searchLower = searchValue.toLowerCase()

      return translatedText.includes(searchLower) || item.key.toLowerCase().includes(searchLower)
    })

    setMetricList(filteredItems || [])
  }

  const onResetData = () => {
    if (!data) {
      return
    }

    const uniqueMetrics = [...new Set(settings?.metrics || [])]
    const selectedInOrder = uniqueMetrics
      .map((metricKey) => data.find((item) => item.key === metricKey))
      .filter((item): item is MetricMeta => item !== undefined)

    setMetricList(data.filter((dt) => !uniqueMetrics.includes(dt.key)))
    setSelectedMetricList(selectedInOrder || [])
  }

  const onClearAll = () => {
    setSelectedMetricList([])
    setMetricList(data || [])
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (active.id !== over?.id) {
      setSelectedMetricList((items) => {
        const oldIndex = items.findIndex((item) => item.key === active.id)
        const newIndex = items.findIndex((item) => item.key === over?.id)

        return arrayMove(items, oldIndex, newIndex)
      })
    }
  }

  function SortableItem({ id, label }: SortableItemProps) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
      id,
    })
    const isActiveSorted = sortSetting?.key === id
    const hasThreshold = thresholds?.some(
      (th) => th.metricKey === id && th.enabled && th.rules.length > 0,
    )

    return (
      <div
        className={classNames("metric-selected-item", { isDragging, "is-sorted": isActiveSorted })}
        ref={setNodeRef}
        style={{
          transform: CSS.Transform.toString(transform),
          transition,
        }}
        {...attributes}>
        <span className="metric-left-section">
          <span {...listeners}>
            <Icon type="list-drag" color={colors.neutral.grey[600]} />
          </span>
          <p>{label}</p>
          {hasThreshold && (
            <Tooltip title={t("thresholdConfigured")} size="small">
              <span className="threshold-indicator" />
            </Tooltip>
          )}
        </span>
        <span className="metric-right-section">
          {onThresholdClick && (
            <button
              className={classNames("threshold-btn", { active: hasThreshold })}
              onClick={() => onThresholdClick(id, label)}
              title={t("threshold")}>
              <Icon
                type="filter"
                size={14}
                color={hasThreshold ? colors.primary[700] : colors.neutral.grey[500]}
              />
            </button>
          )}
          {sortSetting && onSortChange && (
            <>
              {isActiveSorted ? (
                <div
                  className="sort-indicator"
                  onClick={() => {
                    onSortChange({
                      key: id,
                      order: sortSetting.order === "asc" ? "desc" : "asc",
                    })
                  }}>
                  <span className="sort-arrow">{sortSetting.order === "asc" ? "↑" : "↓"}</span>
                  <span className="sort-text">
                    {t(sortSetting.order === "asc" ? "ascending" : "descending")}
                  </span>
                </div>
              ) : (
                <button
                  className="sort-change-button"
                  onClick={() => {
                    onSortChange({ key: id, order: "asc" })
                  }}>
                  {t("button.setAsPrimarySort")}
                </button>
              )}
            </>
          )}
          <IconButton
            type="ghost"
            size="xxs"
            icon="close"
            onClick={() => {
              const updatedSelectedList = selectedMetricList.filter((item) => item.key !== id)
              setSelectedMetricList(updatedSelectedList)
              onSearchMetric(
                data?.filter((item) => !updatedSelectedList.some((sm) => sm.key === item.key)),
              )
            }}
          />
        </span>
      </div>
    )
  }

  return (
    <div className="MetricSelectorTable">
      <div className="MetricSelectorLeftCont">
        <div className="metric-selector-search-wrapper">
          <p>
            {t("metrics")} ({metricList.length})
          </p>
          <TextField
            type="search"
            size="small"
            placeholder={t("search")}
            appendIcon={<Icon type="search" size={24} color={colors.neutral.grey[400]} />}
            onChange={(e) => setSearchValue(e.target.value)}
          />
        </div>
        <div className="metric-selector-list-wrapper">
          {unselectedAdditionalItems && unselectedAdditionalItems.length > 0 && (
            <>
              {unselectedAdditionalItems.map((item) => (
                <div
                  key={item.key}
                  className="metric-selector-item"
                  onClick={() => {
                    setSelectedAdditionalKeys((prev) => [...prev, item.key])
                  }}>
                  <Icon type="add" size={20} color={colors.neutral.grey[600]} />
                  <p>{t(item.label)}</p>
                </div>
              ))}
            </>
          )}
          {metricList.map((metric) => (
            <div
              key={metric.key}
              className="metric-selector-item"
              onClick={() => {
                setSelectedMetricList([...selectedMetricList, metric])
                setMetricList(metricList.filter((item) => item.key !== metric.key))
              }}>
              <Icon type="add" size={20} color={colors.neutral.grey[600]} />
              <p>{translateMetric(metric)}</p>
            </div>
          ))}
        </div>
      </div>
      <Icon type="switch" color={colors.neutral.grey[600]} />
      <div className="MetricSelectorRightCont">
        <div className="metric-selector-reset-wrapper">
          <p>
            {t("selectedMetrics")} ({selectedMetricList.length})
          </p>
          <span>
            <BasicButton type="ghost" size="md" onClick={onClearAll}>
              <p className="metric-clear-all-button">{t("button.clearAll")}</p>
            </BasicButton>
            <BasicButton type="default-outlined" size="sm" onClick={onResetData}>
              <div className="metric-reset-button">
                <Icon type="refresh" size={24} color={colors.neutral.grey[600]} />
                <p>{t("button.reset")}</p>
              </div>
            </BasicButton>
          </span>
        </div>
        <div
          className={classNames("metric-selector-list-wrapper", {
            isError: !selectedMetricList.length,
          })}>
          {/* Fixed columns at the top - not draggable */}
          {fixedColumns.map((column) => {
            const isActiveSorted = sortSetting?.key === column.key

            return (
              <div
                key={column.key}
                className={classNames("metric-fixed-item", { "is-sorted": isActiveSorted })}>
                <span className="icon-placeholder" />
                <p>{t(column.label)}</p>
                {sortSetting && onSortChange && (
                  <>
                    {isActiveSorted ? (
                      <div
                        className="sort-indicator"
                        onClick={() => {
                          onSortChange({
                            key: column.key,
                            order: sortSetting.order === "asc" ? "desc" : "asc",
                          })
                        }}>
                        <span className="sort-arrow">
                          {sortSetting.order === "asc" ? "↑" : "↓"}
                        </span>
                        <span className="sort-text">
                          {t("sort") + " "}
                          {t(sortSetting.order === "asc" ? "ascending" : "descending")}
                        </span>
                      </div>
                    ) : (
                      <button
                        className="sort-change-button"
                        onClick={() => {
                          onSortChange({ key: column.key, order: "asc" })
                        }}>
                        {t("button.setAsPrimarySort")}
                      </button>
                    )}
                  </>
                )}
              </div>
            )
          })}

          {/* Additional items (time columns) - not draggable */}
          {selectedAdditionalKeys.length > 0 && (
            <>
              {selectedAdditionalKeys.map((itemKey) => {
                const item = additionalItems?.find((ai) => ai.key === itemKey)
                if (!item) return null

                return (
                  <div key={item.key} className="metric-fixed-item">
                    <span className="icon-placeholder" />
                    <p>{t(item.label)}</p>
                    <IconButton
                      type="ghost"
                      size="xxs"
                      icon="close"
                      onClick={() => {
                        setSelectedAdditionalKeys((prev) => prev.filter((k) => k !== item.key))
                      }}
                    />
                  </div>
                )
              })}
            </>
          )}

          {/* Draggable metric items */}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}>
            <SortableContext
              items={selectedMetricList.map((selected) => selected.key)}
              strategy={verticalListSortingStrategy}>
              {selectedMetricList.map((metric) => (
                <SortableItem key={metric.key} id={metric.key} label={translateMetric(metric)} />
              ))}
            </SortableContext>
          </DndContext>
        </div>
        {!selectedMetricList.length && (
          <p className="metric-selected-error-msg">{t("rules.required")}</p>
        )}
      </div>
    </div>
  )
}
